import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CatalogPage() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ category: '', item: '', unit: 'each', price: 0, labor: 0 });
  const [msg, setMsg] = useState('');

  const fetchItems = useCallback(async () => {
    const res = await fetch(`${API}/api/catalog/items`, { credentials: 'include' });
    if (res.ok) {
      const data = await res.json();
      setItems(data);
      setCategories([...new Set(data.map(i => i.category))]);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  const showMsg = (t) => { setMsg(t); setTimeout(() => setMsg(''), 3000); };

  const handleSave = async () => {
    const url = editing ? `${API}/api/catalog/items/${editing}` : `${API}/api/catalog/items`;
    const res = await fetch(url, { method: editing ? 'PUT' : 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
    if (res.ok) { showMsg(editing ? 'Actualizado' : 'Creado'); setEditing(null); setAdding(false); fetchItems(); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar?')) return;
    await fetch(`${API}/api/catalog/items/${id}`, { method: 'DELETE', credentials: 'include' });
    fetchItems();
  };

  const filtered = activeTab === 'all' ? items : items.filter(i => i.category === activeTab);

  return (
    <div data-testid="catalog-page" className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">Catalogo de Precios</h1>
          <p className="text-slate-400 text-sm">{items.length} items | Alimenta cotizaciones CRM y B2B</p>
        </div>
        <Button data-testid="add-item-btn" onClick={() => { setAdding(true); setEditing(null); setForm({ category: categories[0] || 'CCTV', item: '', unit: 'each', price: 0, labor: 0 }); }}>Agregar Item</Button>
      </div>
      {msg && <div className="bg-blue-900/30 border border-blue-700 text-blue-200 px-4 py-2 rounded text-sm">{msg}</div>}
      {adding && (
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader><CardTitle className="text-white">{editing ? 'Editar' : 'Nuevo'} Item</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              <div><label className="text-slate-400 text-xs">Categoria</label><input className="w-full bg-slate-900 text-white border border-slate-600 rounded px-3 py-2 text-sm mt-1" value={form.category} onChange={e => setForm({...form, category: e.target.value})} /></div>
              <div className="col-span-2"><label className="text-slate-400 text-xs">Item</label><input className="w-full bg-slate-900 text-white border border-slate-600 rounded px-3 py-2 text-sm mt-1" value={form.item} onChange={e => setForm({...form, item: e.target.value})} /></div>
              <div><label className="text-slate-400 text-xs">Unidad</label><input className="w-full bg-slate-900 text-white border border-slate-600 rounded px-3 py-2 text-sm mt-1" value={form.unit} onChange={e => setForm({...form, unit: e.target.value})} /></div>
              <div><label className="text-slate-400 text-xs">Material $</label><input type="number" className="w-full bg-slate-900 text-white border border-slate-600 rounded px-3 py-2 text-sm mt-1" value={form.price} onChange={e => setForm({...form, price: parseFloat(e.target.value) || 0})} /></div>
              <div><label className="text-slate-400 text-xs">Labor $</label><input type="number" className="w-full bg-slate-900 text-white border border-slate-600 rounded px-3 py-2 text-sm mt-1" value={form.labor} onChange={e => setForm({...form, labor: parseFloat(e.target.value) || 0})} /></div>
            </div>
            <div className="flex gap-2 mt-3">
              <Button onClick={handleSave} className="bg-green-600 hover:bg-green-700">Guardar</Button>
              <Button variant="outline" onClick={() => { setAdding(false); setEditing(null); }}>Cancelar</Button>
            </div>
          </CardContent>
        </Card>
      )}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">Todos ({items.length})</TabsTrigger>
          {categories.map(c => <TabsTrigger key={c} value={c}>{c}</TabsTrigger>)}
        </TabsList>
        <TabsContent value={activeTab} className="mt-4">
          <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-900 text-slate-400 text-xs uppercase">
                <th className="px-4 py-3 text-left">Categoria</th><th className="px-4 py-3 text-left">Item</th><th className="px-4 py-3">Unidad</th>
                <th className="px-4 py-3 text-right">Material</th><th className="px-4 py-3 text-right">Labor</th><th className="px-4 py-3 text-right">Total</th><th className="px-4 py-3">Acciones</th>
              </tr></thead>
              <tbody>{filtered.map(item => (
                <tr key={item.id} className="border-t border-slate-700 hover:bg-slate-800/80">
                  <td className="px-4 py-2 text-slate-300">{item.category}</td>
                  <td className="px-4 py-2 text-white font-medium">{item.item}</td>
                  <td className="px-4 py-2 text-slate-400 text-center">{item.unit}</td>
                  <td className="px-4 py-2 text-right text-slate-300">${item.price?.toFixed(2)}</td>
                  <td className="px-4 py-2 text-right text-slate-300">${item.labor?.toFixed(2)}</td>
                  <td className="px-4 py-2 text-right text-green-400 font-semibold">${item.total?.toFixed(2)}</td>
                  <td className="px-4 py-2 text-center">
                    <Button size="sm" variant="ghost" className="text-xs text-blue-400" onClick={() => { setEditing(item.id); setForm({ category: item.category, item: item.item, unit: item.unit, price: item.price, labor: item.labor }); setAdding(true); }}>Editar</Button>
                    <Button size="sm" variant="ghost" className="text-xs text-red-400" onClick={() => handleDelete(item.id)}>X</Button>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
